/**
 * Tiny inline-SVG sparkline. Used on the profile home StatusCard and as
 * a header strip on the Feed / History detail pages. Stays here in
 * `components/YouTube` rather than `theme/` because it's a render
 * primitive, not a design token, and the bundled file weight (sub-1kB
 * gzipped) doesn't warrant a chart library.
 */
import React from 'react';

interface Props {
  data: number[];
  width?: number;
  height?: number;
  stroke?: string;
}

export function Sparkline({
  data,
  width = 120,
  height = 28,
  stroke = 'currentColor',
}: Props): React.JSX.Element {
  if (!data || data.length < 2) {
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke={stroke}
          strokeWidth={2}
          strokeLinecap="round"
          opacity={0.3}
        />
      </svg>
    );
  }
  const max = Math.max(...data, 5);
  const step = width / (data.length - 1);
  const points = data
    .map((v, i) => `${i * step},${height - (v / max) * (height - 4) - 2}`)
    .join(' ');
  const lastX = (data.length - 1) * step;
  const lastY = height - (data[data.length - 1] / max) * (height - 4) - 2;
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{overflow: 'visible'}}
    >
      <polyline
        points={points}
        fill="none"
        stroke={stroke}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastX} cy={lastY} r={3} fill={stroke} />
    </svg>
  );
}
